"use client";

import styles from "./application-modal.module.css";
import { MdClose } from "react-icons/md";
import {
  FiUser,
  FiBriefcase,
  FiCalendar,
  FiHome,
  FiMessageSquare,
} from "react-icons/fi";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { useApplicationStore } from "../applications-store";
import { useTranslations } from "next-intl";
import { PiBuildingOfficeBold } from "react-icons/pi";
import { useDialogRouting } from "@/hooks/use-dialog-routing";

type Props = {
  dialogRef: RefObject<HTMLDivElement | null>;
};

export default function ApplicationModal({ dialogRef }: Props) {
  const [message, setMessage] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [decisionMessage, setDecisionMessage] = useState("");
  const { userData } = useUserAuthDataContext();
  const [deciding, setDeciding] = useState(false);
  const t = useTranslations("AgencyApplicationModal");
  const sendMessage = useApplicationStore((state) => state.sendMessage);
  const updateApplicationStatus = useApplicationStore(
    (state) => state.updateApplicationStatus,
  );
  const {
    isApplicationModalOpen,
    currentApplication,
    isApplicationLoading: isApplicationloading,
    closeApplication,
  } = useApplicationStore();

  useEffect(() => {
    if (!isApplicationModalOpen || !currentApplication) {
      setMessage("");
      setDecisionMessage("");
      setIsSendingMessage(false);
      setDeciding(false);
    }
  }, [isApplicationModalOpen, currentApplication?.request_id]);

  useDialogRouting(
    "ApplicationModal",
    isApplicationModalOpen,
    closeApplication,
  );
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isApplicationModalOpen) {
      closeBtnRef.current?.focus();
    }
  }, [isApplicationModalOpen]);

  if (isApplicationloading) {
    return <ApplicationModalSkeleton dialogRef={dialogRef} isAdmin={true} />;
  }

  if (!currentApplication) return null;

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        e.stopPropagation();
        closeApplication();
      }}
    >
      <div
        ref={dialogRef}
        className={`${styles.modal} ${styles.show}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="agency-application-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* === Header === */}
        <header className={styles.header}>
          <div className={styles.headerTexts}>
            <h2 id="agency-application-title" className={styles.title}>
              {currentApplication.agency_name}
            </h2>
            <p className={styles.subtitle}>
              {t("submittedOn")}{" "}
              {new Date(currentApplication.created_at).toLocaleDateString()}
            </p>
          </div>

          <button
            ref={closeBtnRef}
            className={styles.closeBtn}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              closeApplication();
            }}
            aria-label={t("closeModal")}
          >
            <MdClose />
          </button>
        </header>
        <div className={styles.divider} />
        {currentApplication.reviewed_at &&
          currentApplication.reviewed_by_name && (
            <div className={styles.reviewedInfo}>
              <img
                src={
                  currentApplication.reviewed_by_photo_url ||
                  "/avatar-placeholder.png"
                }
                alt={currentApplication.reviewed_by_name}
                className={styles.reviewedAvatar}
              />
              <span>
                {t("reviewdBy")}{" "}
                <strong>{currentApplication.reviewed_by_name}</strong> {t("on")}{" "}
                {new Date(currentApplication.reviewed_at).toLocaleDateString()}
              </span>
            </div>
          )}
        {/* === Status === */}
        <div className={styles.statusRow}>
          <span className={styles.statusLabel}>{t("applicationStatus")}</span>
          <span
            className={`${styles.statusBadge} ${
              styles[currentApplication.status]
            }`}
            aria-live="polite"
          >
            {t(currentApplication.status)}
          </span>
        </div>
        {/* === Overview === */}
        <section className={styles.overview}>
          <div className={styles.kv}>
            <span className={styles.kvIcon}>
              <FiUser />
            </span>
            <div className={styles.kvText}>
              <span className={styles.k}>{t("applicant")}</span>
              <span className={styles.v}>{currentApplication.full_name}</span>
            </div>
          </div>

          <div className={styles.kv}>
            <span className={styles.kvIcon}>
              <FiBriefcase />
            </span>
            <div className={styles.kvText}>
              <span className={styles.k}>{t("role")}</span>
              <span className={styles.v}>
                {t(`roles.${currentApplication.role}`)}
              </span>
            </div>
          </div>

          <div className={styles.kv}>
            <span className={styles.kvIcon}>
              <FiCalendar />
            </span>
            <div className={styles.kvText}>
              <span className={styles.k}>{t("founded")}</span>
              <span className={styles.v}>
                {currentApplication.founded_year}
              </span>
            </div>
          </div>

          <div className={styles.kv}>
            <span className={styles.kvIcon}>
              <FiHome />
            </span>
            <div className={styles.kvText}>
              <span className={styles.k}>{t("avgListings")}</span>
              <span className={styles.v}>
                {currentApplication.avg_listings}
              </span>
            </div>
          </div>

          <div className={styles.kv}>
            <span className={styles.kvIcon}>
              <FiHome />
            </span>
            <div className={styles.kvText}>
              <span className={styles.k}>{t("priceRange")}</span>
              <span className={styles.v}>
                {" "}
                {currentApplication.price_range != ""
                  ? t(`priceRanges.${currentApplication.price_range}`)
                  : "-"}
              </span>
            </div>
          </div>
        </section>
        <div className={`${styles.kv} ${styles.kvAddress}`}>
          <span className={styles.kvIcon}>
            <PiBuildingOfficeBold />
          </span>
          <div className={styles.kvText}>
            <span className={styles.k}>Address</span>
            <span className={styles.v}>{currentApplication.address}</span>
          </div>
        </div>
        {/* === Details Grid === */}
        <section className={styles.grid}>
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>{t("specializations")}</h3>
            <ul className={styles.list}>
              {Object.entries(currentApplication.specializations)
                .filter(([, v]) => v)
                .map(([k]) => (
                  <li key={k}>{t(k)}</li>
                ))}
            </ul>
          </div>

          <div className={styles.card}>
            <h3 className={styles.cardTitle}>{t("acquisition")}</h3>
            <ul className={styles.list}>
              {Object.entries(currentApplication.acquisition)
                .filter(([, v]) => v)
                .map(([k]) => (
                  <li key={k}>{t(k)}</li>
                ))}
            </ul>
          </div>

          <div className={styles.card}>
            <h3 className={styles.cardTitle}>{t("locations")}</h3>
            <p className={styles.cardText}>{currentApplication.locations}</p>
          </div>

          <div className={`${styles.card} ${styles.cardWide}`}>
            <h3 className={styles.cardTitle}>{t("motivation")}</h3>
            <p className={styles.cardText}>{currentApplication.motivation}</p>
          </div>
        </section>
        {/* === Comments === */}
        <section className={styles.comments}>
          <h3 className={styles.commentsTitle}>
            <FiMessageSquare /> {t("messages")}
          </h3>

          {currentApplication.notes.length === 0 ? (
            <p className={styles.noComments}>{t("adminNoMessages")}</p>
          ) : (
            <ul className={styles.commentsList}>
              {currentApplication.notes.map((c, idx) => (
                <li key={idx} className={styles.comment}>
                  <img
                    src={c.author_photo_url}
                    alt={t("profilePhotoAlt", { name: c.author_name })}
                    className={styles.avatar}
                  />
                  <div>
                    <div className={styles.commentHeader}>
                      <strong>{c.author_name}</strong>
                      <span>{new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className={styles.commentText}>{c.message}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        {/* === Reply box === */}
        {currentApplication.status === "pending" && (
          <div className={styles.replyBox}>
            <img
              src={userData.user_photo}
              alt={t("profilePhotoAlt", { name: userData.complete_name })}
              className={styles.replyAvatar}
            />

            <div className={styles.replyContent}>
              <label htmlFor="admin-message" className={styles.srOnly}>
                {t("adminMessageLabel")}
              </label>
              <textarea
                id="admin-message"
                className={styles.replyInput}
                placeholder={t("adminAdditionalInformations")}
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />

              <div className={styles.replyActions}>
                <span className={styles.replyHint}>
                  {t("adminMessageVisibleToUser")}
                </span>

                <button
                  className={styles.replyButton}
                  disabled={!message.trim() || isSendingMessage}
                  aria-disabled={!message.trim() || isSendingMessage}
                  onClick={async () => {
                    setIsSendingMessage(true);
                    const ok = await sendMessage(
                      currentApplication.request_id,
                      message,
                      userData.sessionId,
                      userData.complete_name,
                      userData.user_photo ?? "",
                      userData.sub ?? "",
                    );
                    if (ok) setMessage("");
                    setIsSendingMessage(false);
                  }}
                >
                  {t("sendMessage")}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* === Admin Actions === */}
        {currentApplication.status === "pending" && (
          <section className={styles.adminActions}>
            <h3 className={styles.adminTitle}>Final decision</h3>

            <label htmlFor="final-decision" className={styles.srOnly}>
              {t("finalDecisionNote")}
            </label>
            <textarea
              id="final-decision"
              className={styles.adminTextarea}
              placeholder={t("adminAddNote")}
              rows={3}
              value={decisionMessage}
              onChange={(e) => setDecisionMessage(e.target.value)}
            />

            <div className={styles.adminButtons}>
              <span className={styles.adminHint}>
                {t("adminDecisionWarning")}
              </span>
              <button
                className={styles.rejectBtn}
                disabled={
                  deciding ||
                  !decisionMessage.trim() ||
                  currentApplication.status != "pending"
                }
                aria-disabled={
                  deciding ||
                  !decisionMessage.trim() ||
                  currentApplication.status != "pending"
                }
                onClick={async () => {
                  setDeciding(true);
                  try {
                    const ok = await updateApplicationStatus(
                      currentApplication.request_id,
                      "rejected",
                      decisionMessage,
                      userData.sessionId,
                      userData.sub ?? "",
                      userData.user_photo ?? "",
                    );

                    if (ok) {
                      setDecisionMessage("");
                    }
                  } finally {
                    setDeciding(false);
                  }
                }}
              >
                {t("reject")}
              </button>

              <button
                className={styles.approveBtn}
                disabled={
                  deciding ||
                  !decisionMessage.trim() ||
                  currentApplication.status != "pending"
                }
                aria-disabled={
                  deciding ||
                  !decisionMessage.trim() ||
                  currentApplication.status != "pending"
                }
                onClick={async () => {
                  setDeciding(true);
                  try {
                    const ok = await updateApplicationStatus(
                      currentApplication.request_id,
                      "approved",
                      decisionMessage,
                      userData.sessionId,
                      userData.sub ?? "",
                      userData.user_photo ?? "",
                    );

                    if (ok) {
                      setDecisionMessage("");
                    }
                  } finally {
                    setDeciding(false);
                  }
                }}
              >
                {t("approve")}
              </button>
            </div>
          </section>
        )}
        {currentApplication.status !== "pending" && (
          <section
            className={`${styles.finalNotice} ${
              styles[currentApplication.status]
            }`}
          >
            <h3 className={styles.finalTitle}>
              {t("application")} {t(currentApplication.status)}
            </h3>

            <p className={styles.finalText}>
              {t("applicationHasBeen")}{" "}
              <strong>{t(currentApplication.status)}</strong>{" "}
              {t("finalDecision")}
            </p>

            {currentApplication.reviewed_at &&
              currentApplication.reviewed_by_name && (
                <div className={styles.finalReviewer}>
                  <img
                    src={
                      currentApplication.reviewed_by_photo_url ||
                      "/avatar-placeholder.png"
                    }
                    alt={currentApplication.reviewed_by_name}
                    className={styles.finalReviewerAvatar}
                  />

                  <div className={styles.finalReviewerText}>
                    <span className={styles.finalReviewerName}>
                      {currentApplication.reviewed_by_name}
                    </span>
                    <span className={styles.finalReviewerMeta}>
                      {t("reviewedOn")}{" "}
                      {new Date(
                        currentApplication.reviewed_at,
                      ).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              )}
          </section>
        )}
      </div>
    </div>
  );
}

export function ApplicationModalSkeleton({
  dialogRef,
  isAdmin,
}: {
  dialogRef: RefObject<HTMLDivElement | null>;
  isAdmin: boolean;
}) {
  return (
    <div className={styles.overlay}>
      <div ref={dialogRef} className={`${styles.modal} ${styles.show}`}>
        {/* === Header === */}
        <header className={styles.header}>
          <div className={styles.headerTexts}>
            <h2
              className={`${styles.title} ${styles.isLoading} ${styles.isLoadingTitle}`}
            />
            <p
              className={`${styles.subtitle} ${styles.isLoading} ${styles.isLoadingText}`}
            />
          </div>

          <button className={styles.closeBtn} disabled>
            <MdClose />
          </button>
        </header>

        <div className={styles.divider} />

        {/* === Status === */}
        <div className={styles.statusRow}>
          <span
            className={`${styles.statusLabel} ${styles.isLoading} ${styles.isLoadingStatusLabel}`}
          />
          <span
            className={`${styles.statusBadge} ${styles.isLoading} ${styles.isLoadingStatusBadge}`}
          />
        </div>

        {/* === Overview === */}
        <section
          className={`${styles.overview} ${
            !isAdmin && styles.isLoadingOverviewUser
          }`}
        >
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={styles.kv}>
              <span className={styles.kvIcon} />
              <div className={styles.kvText}>
                <span
                  className={`${styles.k} ${styles.isLoading} ${styles.isLoadingKey}`}
                />
                <span
                  className={`${styles.v} ${styles.isLoading} ${styles.isLoadingValue}`}
                />
              </div>
            </div>
          ))}
        </section>
        {isAdmin && (
          <div className={`${styles.kv} ${styles.kvAddress}`}>
            <span className={styles.kvIcon} />
            <div className={styles.kvText}>
              <span
                className={`${styles.k} ${styles.isLoading} ${styles.isLoadingKey}`}
              />
              <span
                className={`${styles.v} ${styles.isLoading} ${styles.isLoadingValue}`}
              />
            </div>
          </div>
        )}

        {/* === Details Grid === */}
        <section className={styles.grid}>
          <div className={styles.card}>
            <h3
              className={`${styles.cardTitle} ${styles.isLoading} ${styles.isLoadingText}`}
            />
            <div
              className={`${styles.isLoading} ${styles.isLoadingCardList}`}
            />
          </div>

          <div className={styles.card}>
            <h3
              className={`${styles.cardTitle} ${styles.isLoading} ${styles.isLoadingText}`}
            />
            <div
              className={`${styles.isLoading} ${styles.isLoadingCardList}`}
            />
          </div>

          <div className={styles.card}>
            <h3
              className={`${styles.cardTitle} ${styles.isLoading} ${styles.isLoadingText}`}
            />
            <div
              className={`${styles.isLoading} ${styles.isLoadingCardText}`}
            />
          </div>

          <div className={`${styles.card} ${styles.cardWide}`}>
            <h3
              className={`${styles.cardTitle} ${styles.isLoading} ${styles.isLoadingText}`}
            />
            <div
              className={`${styles.isLoading} ${styles.isLoadingCardWide}`}
            />
          </div>
        </section>

        {/* === Comments === */}
        <section className={styles.comments}>
          <h3
            className={`${styles.commentsTitle} ${styles.isLoading} ${styles.isLoadingText}`}
          />

          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className={styles.comment}>
              <div
                className={`${styles.avatar} ${styles.isLoading} ${styles.isLoadingAvatar}`}
              />
              <div style={{ flex: 1 }}>
                <div
                  className={`${styles.isLoading} ${styles.isLoadingCommentHeader}`}
                />
                <div
                  className={`${styles.isLoading} ${styles.isLoadingCommentText}`}
                />
              </div>
            </div>
          ))}
        </section>

        {/* === Reply box (disabled skeleton) === */}
        <div className={styles.replyBox}>
          <div
            className={`${styles.replyAvatar} ${styles.isLoading} ${styles.isLoadingAvatar}`}
          />
          <div className={styles.replyContent}>
            <div
              className={`${styles.isLoading} ${styles.isLoadingReplyInput}`}
            />
          </div>
        </div>

        {/* === Admin actions === */}
        {isAdmin && (
          <section className={styles.adminActions}>
            <h3
              className={`${styles.adminTitle} ${styles.isLoading} ${styles.isLoadingText}`}
            />
            <div
              className={`${styles.isLoading} ${styles.isLoadingAdminTextarea}`}
            />
          </section>
        )}
      </div>
    </div>
  );
}
